import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import {
    MatActionList,
    MatListItem,
    MatListItemIcon,
    MatListItemMeta,
    MatListItemTitle,
    MatListSubheaderCssMatStyler,
    MatNavList
} from '@angular/material/list';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { AuthorizationService } from '../../../auth/authorization/services/authorization.service';
import { AppSwitcherService } from './app-switcher.service';
import { ExperimentalFeatureService } from '../../experimental-features/experimental-feature.service';
import { ExperimentalTagComponent } from '../../experimental-features/experimental-tag.component';
import { NavigationService } from '../../../services/navigation.service';
import { SphereAppsService } from '../../../services/__sphere-apps__.service';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatTooltip } from '@angular/material/tooltip';
import { AppSwitcherStore } from './app-switcher.store';
import { SphereAppCategory } from '../../../constants/app-categories';
import { AppRoute } from '../../../types/app-route';

@Component({
    templateUrl: './app-switcher.component.html',
    styleUrl: './app-switcher.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatCard,
        MatCardContent,
        RouterLink,
        MatNavList,
        MatListItem,
        MatListItemTitle,
        MatListItemIcon,
        MatIcon,
        MatCardHeader,
        MatIconButton,
        MatListItemMeta,
        ExperimentalTagComponent,
        NgTemplateOutlet,
        MatListSubheaderCssMatStyler,
        MatActionList,
        MatDivider,
        NgClass,
        MatTooltip
    ],
    providers: [AppSwitcherStore]
})
export class AppSwitcherComponent implements AfterViewInit {
    private readonly authorization = inject(AuthorizationService);
    private readonly experimentalFeatures = inject(ExperimentalFeatureService);
    private readonly nav = inject(NavigationService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly store = inject(AppSwitcherStore);

    private readonly appSwitcher = inject(AppSwitcherService);
    private readonly sphereApps = inject(SphereAppsService);

    public readonly legacyMode = signal<boolean>(true);

    public readonly selectedCategory = this.store.selectedCategory;
    public readonly apps = this.authorization.getAccessibleSphereApps(this.nav.apps, { featured: false });
    public readonly featuredApps = this.authorization.getAccessibleSphereApps(this.nav.apps, { featured: true });
    public readonly isVisible = signal<boolean>(false);

    public readonly sphereAppCategories = this.sphereApps.appCategories;
    public readonly appsForSelectedCategory = computed(() => {
        return this.sphereApps.appsForSelectedCategory(this.selectedCategory())();
    });

    public readonly hasFavorites = computed(() => {
        return this.appsForSelectedCategory()?.favorites?.length > 0 || false;
    });
    public readonly hasNonFavorites = computed(() => {
        return this.appsForSelectedCategory()?.apps?.length > 0 || false;
    });
    public readonly hasApps = computed(() => {
        return this.appsForSelectedCategory()?.all?.length > 0 || false;
    });

    public readonly hasAlphaAccess = this.experimentalFeatures.hasAlphaFeatureAccess;
    public readonly hasBetaAccess = this.experimentalFeatures.hasBetaFeatureAccess;

    ngAfterViewInit() {
        this.isVisible.set(true);
    }

    getLogo(logoUri: string): SafeStyle {
        return this.sanitizer.bypassSecurityTrustStyle(`url("${logoUri}")`);
    }

    selectCategory(category: SphereAppCategory | 'favorites' | null) {
        if (!category) {
            return;
        }
        this.store.selectCategory(category);
    }

    toggleFavorite(event: MouseEvent, app: AppRoute, isFavorite: boolean) {
        event.stopPropagation();
        event.preventDefault();
        if (isFavorite) {
            this.sphereApps.removeFavoriteApp(app);
        } else {
            this.sphereApps.addFavoriteApp(app);
        }
    }

    closeSelf() {
        this.isVisible.set(false);
        setTimeout(() => {
            this.appSwitcher.closeAppSwitcher();
        }, 150);
    }
}
